import React, { useEffect, useState } from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewArrivalsSpotlight = () => {
  const [featured, setFeatured] = useState([]);
  const [newCount, setNewCount] = useState(0);

  useEffect(() => {
    fetch(`${API_URL}/api/products`)
      .then((r) => r.json())
      .then((data) => {
        if (!Array.isArray(data)) return;
        const fresh = data.filter((p) => p.isNew);
        setNewCount(fresh.length);
        const inStock = fresh.filter((p) => typeof p.stock !== 'number' || p.stock > 0);
        setFeatured(inStock.slice(-4).reverse());
      })
      .catch(() => setFeatured([]));
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  if (featured.length === 0) return null;

  const lowStock = featured.some((p) => typeof p.stock === 'number' && p.stock <= 5);
  const label = (slug) => (slug || '').replace(/-/g, ' ');

  return (
    <section className="bg-[#001B2A] py-14 sm:py-16" data-testid="new-arrivals-spotlight">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 bg-[#7B1E2B] text-white text-xs font-bold tracking-[0.25em] px-4 py-1.5 rounded-full mb-5">
              <Sparkles className="h-3.5 w-3.5" /> JUST ARRIVED
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-white mb-4">
              Fresh <span className="text-[#E6D7B3]">New Arrivals</span>
            </h2>
            <p className="text-white/70 text-base sm:text-lg mb-3 max-w-lg">
              {newCount <= 30 ? `${newCount} brand-new ${newCount === 1 ? 'piece has' : 'pieces have'} just landed` : 'New pieces just landed'} — bold patterns,
              coordinated sets, and statement looks picked by Johnnie himself.
              {lowStock && (
                <span className="text-[#E6D7B3] font-semibold"> Some are one-of-a-kind — when they're gone, they're gone.</span>
              )}
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <button
                data-testid="spotlight-shop-new"
                onClick={() => scrollTo('new-arrivals')}
                className="inline-flex items-center gap-2 bg-[#7B1E2B] text-white px-7 py-3.5 rounded-md font-semibold hover:bg-[#E6D7B3] hover:text-[#001B2A] transition-colors"
              >
                Shop New Arrivals <ArrowRight className="h-4 w-4" />
              </button>
              <button
                data-testid="spotlight-browse-category"
                onClick={() => scrollTo(featured[0].category)}
                className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-3.5 rounded-md font-semibold capitalize hover:bg-white/10 transition-colors"
              >
                Browse {label(featured[0].category)}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {featured.map((p, i) => (
              <button
                key={p.id}
                data-testid={`spotlight-item-${p.id}`}
                onClick={() => scrollTo(p.category)}
                className={`relative rounded-lg overflow-hidden group text-left ${i % 2 === 1 ? 'translate-y-4' : ''}`}
              >
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute top-3 left-3 bg-[#7B1E2B] text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
                  NEW
                </span>
                {typeof p.stock === 'number' && p.stock <= 5 && (
                  <span className="absolute top-3 right-3 bg-orange-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
                    ONLY {p.stock} LEFT
                  </span>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                  <p className="text-white text-xs font-semibold truncate">{p.name}</p>
                  <p className="text-[#E6D7B3] text-sm font-bold">${p.price.toFixed(2)}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewArrivalsSpotlight;
